import { useCallback, useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { transformFiltersV2 } from 'utils/helperMethods';

export interface FilterKeyConfig {
    label: string;
    value: string;
    dimension: string;
    resolveLabelFrom?: string;
}

export interface GlobalFilterState {
    availableFilters: FilterKeyConfig[];
    allCheckedAttribute: Record<string, Set<string>>;
    allSelectedValueForAllAttribute: Record<string, boolean>;
    applyFilterObject: Record<string, Record<string, unknown>>;
    selectedFilter: FilterKeyConfig | null;
    setSelectedFilter: (filter: FilterKeyConfig | null) => void;
    tempApplyFilterObject: Record<string, Record<string, unknown>>;
    setTempApplyFilterObject: (obj: Record<string, Record<string, unknown>>) => void;
    filters: Record<string, any>;
    filterApiVariables: { attribute_type: string; limit: number; filters: Record<string, any> } | undefined;
    resetFilterObject: () => void;
    onFilterSaveClickHandler: (resultObj: {
        allCheckedAttribute: Record<string, Set<string>>;
        allSelectedValueForAllAttribute: Record<string, boolean>;
        applyFilterObject: Record<string, Record<string, unknown>>;
    }) => void;
}

const useGlobalFilterState = (filterKeys: FilterKeyConfig[]): GlobalFilterState => {
    const [selectedFilter, setSelectedFilter] = useState<FilterKeyConfig | null>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<Record<string, Record<string, unknown>>>({});
    const [allCheckedAttribute, setAllCheckedAttribute] = useState<Record<string, Set<string>>>({});
    const [allSelectedValueForAllAttribute, setAllSelectedValueForAllAttribute] = useState<Record<string, boolean>>({});
    const [applyFilterObject, setApplyFilterObject] = useState<Record<string, Record<string, unknown>>>({});
    const [availableFilters, setAvailableFilters] = useState<FilterKeyConfig[]>([]);

    useEffect(() => {
        const allSelectedObj: Record<string, Set<string>> = {};
        const selectAllObj: Record<string, boolean> = {};
        const newApplyFilterObj: Record<string, Record<string, unknown>> = {};
        filterKeys.forEach((filter) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(filterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(newApplyFilterObj);
        setAllSelectedValueForAllAttribute(selectAllObj);
    }, [filterKeys]);

    const resetFilterObject = useCallback(() => {
        const allSelectedObj: Record<string, Set<string>> = {};
        const selectAllObj: Record<string, boolean> = {};
        const newApplyFilterObj: Record<string, Record<string, unknown>> = {};
        filterKeys.forEach((filter) => {
            allSelectedObj[filter.value] = new Set();
            newApplyFilterObj[filter.value] = {};
            selectAllObj[filter.value] = false;
        });
        setAvailableFilters(filterKeys);
        setAllCheckedAttribute(allSelectedObj);
        setApplyFilterObject(cloneDeep(newApplyFilterObj));
        setAllSelectedValueForAllAttribute(selectAllObj);
    }, [filterKeys]);

    const onFilterSaveClickHandler = useCallback(
        (resultObj: {
            allCheckedAttribute: Record<string, Set<string>>;
            allSelectedValueForAllAttribute: Record<string, boolean>;
            applyFilterObject: Record<string, Record<string, unknown>>;
        }) => {
            setAllCheckedAttribute(resultObj.allCheckedAttribute);
            setAllSelectedValueForAllAttribute(resultObj.allSelectedValueForAllAttribute);
            setApplyFilterObject(resultObj.applyFilterObject);
        },
        [],
    );

    const filters = useMemo(() => {
        return transformFiltersV2(applyFilterObject, filterKeys);
    }, [applyFilterObject, filterKeys]);

    const filterApiVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV2(tempApplyFilterObject, filterKeys, selectedFilter.value);
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
            };
        }
        return undefined;
    }, [selectedFilter, tempApplyFilterObject, filterKeys]);

    return {
        availableFilters,
        allCheckedAttribute,
        allSelectedValueForAllAttribute,
        applyFilterObject,
        selectedFilter,
        setSelectedFilter,
        tempApplyFilterObject,
        setTempApplyFilterObject,
        filters,
        filterApiVariables,
        resetFilterObject,
        onFilterSaveClickHandler,
    };
};

export default useGlobalFilterState;
